import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {environment} from "@environments/environment";
import {first} from "rxjs/operators";
import {Observable} from "rxjs";
import {Result} from "@domain/common/http/result";
import {TypeCharge} from "@domain/prestation/type-charge";

/**
 * Service de gestion des Types de Charges
 */
@Injectable()
export class TypesChargesService {
    /** Liste des types de fichiers d'import disponibles */
    private lstTypesFiles: Array<{id: number, libelle: string}> = [
        {id: 16, libelle: 'AirPlus (v2)'},
        {id: 29, libelle: 'AirPlus (v3)'},
        {id: 7, libelle: 'Airplus BTM'},
        {id: 20, libelle: 'ALD - CSV'},
        {id: 10, libelle: 'American Express (BTA D/TXT)'},
        {id: 19, libelle: 'American Express - CSV'},
        {id: 25, libelle: 'American Express - v5'},
        {id: 1, libelle: 'American Express v1 (BTA F/TXT)'},
        {id: 5, libelle: 'American Express v2 (BTA F/TXT)'},
        {id: 14, libelle: 'ARVAL'},
        {id: 24, libelle: 'ARVAL - CSV'},
        {id: 22, libelle: 'BOUYGUES - CSV'},
        {id: 12, libelle: 'CITER - FC12IN'},
        {id: 21, libelle: 'EUROPARCK - CSV'},
        {id: 4, libelle: 'Factures Europcar'},
        {id: 3, libelle: 'Factures SFR'},
        {id: 27, libelle: 'Factures SFR_2'},
        {id: 8, libelle: 'Factures Total GR'},
        {id: 28, libelle: 'Factures Total GR 2'},
        {id: 2, libelle: 'HVAE - CSV'},
        {id: 13, libelle: 'Import paramétrable'},
        {id: 17, libelle: 'LeasePlan - CSV'},
        {id: 26, libelle: 'Overlease - TXT'},
        {id: 18, libelle: 'Selectour - CSV'},
        {id: 15, libelle: 'SIXT'},
        {id: 9, libelle: 'Système Res@car'},
        {id: 23, libelle: 'VINCI - CSV'},
        {id: 30, libelle: 'WebService'}
    ];

    /**
     * Constructeur
     *
     * @param http pour permettre de réaliser les requêtes
     */
    constructor(private http: HttpClient) {
    }

    /**
     * Récupère la liste des types de fichiers d'import
     * @returns La liste des types de fichiers d'import
     */
    get typesFiles(): Array<{id: number, libelle: string}> {
        return this.lstTypesFiles;
    }

    /**
     * Récupère le libellé d'un type de fichier d'import à partir de son id
     * @param id Identifiant du type de fichier d'import
     * @returns Le libellé du type de fichier d'import ou une chaîne vide si non trouvé
     */
    getLibelleTypeFileById(id: number | string): string {
        if (id === null || id === undefined) return '';

        //Conversion en nombre si nécessaire
        const numId = typeof id === 'string' ? parseInt(id, 10) : id;

        const typeFile = this.lstTypesFiles.find(type => type.id === numId);
        //Retour
        return typeFile ? typeFile.libelle : '';
    }

    /**
     * Récupération d'un type de charge par son identifiant
     *
     * @param idTypeCharge Identifiant du type de charge
     * @returns Observable contenant le résultat avec le type de charge
     */
    getTypeChargeById(idTypeCharge: string): Observable<Result> {
        return this.http.get<Result<{typeCharge: TypeCharge}>>(`${environment.baseUrl}/controller/typeCharge/${idTypeCharge}`).pipe(first());
    }

    /**
     * Création d'un nouveau type de charge
     *
     * @param typeCharge Type de charge à créer
     * @returns Observable contenant le résultat de l'opération
     */
    createTypeCharge(typeCharge: TypeCharge): Observable<Result> {
        return this.http.post<Result<{typeCharge: TypeCharge}>>(`${environment.baseUrl}/controller/typeCharge/create`, typeCharge).pipe(first());
    }

    /**
     * Mise à jour d'un type de charge existant
     *
     * @param typeCharge Type de charge à mettre à jour
     * @returns Observable contenant le résultat de l'opération
     */
    updateTypeCharge(typeCharge: TypeCharge): Observable<Result> {
        return this.http.post<Result<{typeCharge: TypeCharge}>>(`${environment.baseUrl}/controller/typeCharge/update`, typeCharge).pipe(first());
    }

    /**
     * Sauvegarde d'un type de charge (création ou modification)
     *
     * @param isCreation Indique si c'est une création ou une modification
     * @param typeCharge Type de charge à sauvegarder
     * @returns Observable contenant le résultat de l'opération
     */
    saveTypeCharge(isCreation: boolean, typeCharge: TypeCharge): Observable<Result> {
        if (isCreation) {
            return this.createTypeCharge(typeCharge);
        } else {
            return this.updateTypeCharge(typeCharge);
        }
    }

    /**
     * Suppression d'un type de charge
     *
     * @param idTypeCharge Identifiant du type de charge à supprimer
     * @returns Observable contenant le résultat de l'opération
     */
    deleteTypeCharge(idTypeCharge: number): Observable<Result> {
        return this.http.post<Result<{success: boolean}>>(`${environment.baseUrl}/controller/typeCharge/delete`, [idTypeCharge]).pipe(first());
    }
}
